import React, { useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  LockKeyhole,
  LockKeyholeOpen,
  Plus,
  TrendingUp,
  ArrowLeft,
} from "lucide-react";

import { cn } from "../lib/utils";
import {
  useMarketAssets,
  useZar8YieldHistory,
  useZar8YieldPosition,
} from "../hooks/query/queries";
import {
  createDefaultZar8LockFlowState,
  getZar8DurationIdFromParts,
  persistZar8LockFlowState,
  ZAR8_LOCK_FLOW_STEPS,
  ZAR8_YIELD_FLOW_TYPES,
} from "../utils/zar8LockFlow";
import { formatFiatWithTwoDecimals } from "../utils/numberFormatter";
import Toaster from "./ui/toaster";
import Zar8YieldPositionChart from "./Zar8YieldPositionChart";
import {
  buildZar8YieldPositionDetails,
  buildZar8YieldPositionHistory,
  buildZar8YieldPositionSeries,
  emitZar8YieldPositionEvent,
  formatZar8YieldBtcValue,
  formatZar8YieldPositionDate,
} from "../utils/zar8YieldPosition";
import {
  getZar8YieldHistoryFromResponse,
  mapZar8YieldPositionToLedgerEntry,
} from "../utils/zar8YieldApi";

const ZAR8_CIRCLE = "/figma/zar8-yield/zar8-circle.svg";
const ZAR8_MARK_SMALL = "/figma/zar8-yield/zar8-mark-small.png";

function SurfaceState({ title, body, actionLabel, onAction }) {
  return (
    <div className="flex min-h-[300px] items-center justify-center rounded-[24px] border border-dashed border-[#EBE9EF] bg-white px-8 text-center">
      <div>
        <p className="text-xl font-semibold leading-7 text-[#423E51]">
          {title}
        </p>
        <p className="mt-2 text-sm leading-5 text-[#6D6584]">{body}</p>
        {actionLabel ? (
          <button
            type="button"
            onClick={onAction}
            className="mt-6 inline-flex h-12 items-center justify-center rounded-full border border-[#F1EDF7] px-6 text-sm font-semibold leading-5 text-[#6D6584] transition-colors hover:bg-[#FAF9FC]"
          >
            {actionLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
}

function AssetChip({ label }) {
  return (
    <div className="flex items-center gap-[5px]">
      <span className="relative block h-[29px] w-[29px] shrink-0">
        <img
          src={ZAR8_CIRCLE}
          alt=""
          aria-hidden="true"
          className="block h-[29px] w-[29px]"
        />
        <img
          src={ZAR8_MARK_SMALL}
          alt=""
          aria-hidden="true"
          className="absolute left-[8px] top-[6px] h-[16px] w-[13px] object-contain"
        />
      </span>
      <span className="text-[14px] leading-5 text-[#676767]">{label}</span>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="min-w-0">
      <p className="text-[16px] leading-6 text-[#6D6584]">{label}</p>
      <p className="mt-3 text-[20px] font-semibold leading-6 text-[#423E51]">
        {value}
      </p>
    </div>
  );
}

function HistoryIcon({ type }) {
  const iconClassName = "h-5 w-5 text-white";

  if (type === "locked") {
    return <LockKeyhole className={iconClassName} />;
  }

  if (type === "unlocked") {
    return <LockKeyholeOpen className={iconClassName} />;
  }

  return <TrendingUp className={iconClassName} />;
}

function HistoryItem({ item }) {
  return (
    <div className="rounded-[16px] border border-[#F1EDF7] p-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#EB3366]">
            <HistoryIcon type={item.type} />
          </div>
          <div className="flex min-w-0 flex-col justify-center">
            <p className="text-[16px] font-semibold leading-6 text-[#423E51]">
              {item.label}
            </p>
            <p className="mt-1 text-[12px] leading-[14px] text-[#6D6584]">
              {formatZar8YieldPositionDate(item.createdAt)}
            </p>
          </div>
        </div>

        <div className="flex shrink-0 flex-col justify-center text-right">
          <p className="text-[16px] font-semibold leading-6 text-[#423E51]">
            {formatFiatWithTwoDecimals(item.amountZar)}
          </p>
          <p className="mt-1 text-[12px] leading-6 text-[#6D6584]">
            ≈ {formatZar8YieldBtcValue(item.amountBtc)}
          </p>
          <p className="text-[10px] font-bold uppercase leading-[14px] tracking-[0.5px] text-[#17C3A5]">
            {item.status}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function Zar8YieldPositionContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { ledgerId = "" } = useParams();
  const positionSnapshot = location.state?.positionSnapshot || null;
  const returnTo = location.state?.returnTo || "/zar8/track";
  const isMockPosition = Boolean(
    positionSnapshot?.rawEntry?.isMock || positionSnapshot?.isMock
  );
  const { data: marketAssets } = useMarketAssets();
  const positionQuery = useZar8YieldPosition(
    ledgerId,
    Boolean(ledgerId) && !isMockPosition
  );
  const historyQueryParams = useMemo(
    () => ({
      positionId: ledgerId,
      page: 1,
      limit: 50,
    }),
    [ledgerId]
  );
  const productHistoryQuery = useZar8YieldHistory(
    historyQueryParams,
    Boolean(ledgerId) && !isMockPosition
  );
  const ledgerEntry = useMemo(
    () => mapZar8YieldPositionToLedgerEntry(positionQuery.data?.position),
    [positionQuery.data?.position]
  );
  const liveProductId =
    ledgerEntry?.productId?._id || positionSnapshot?.productId || "";
  const rawHistoryItems = useMemo(
    () => getZar8YieldHistoryFromResponse(productHistoryQuery.data),
    [productHistoryQuery.data]
  );
  const performanceSeries = useMemo(
    () =>
      buildZar8YieldPositionSeries({
        graphData: {
          performanceData: rawHistoryItems
            .slice()
            .reverse()
            .map((entry) => ({
              label: entry?.date,
              value: entry?.type === "unlocked" ? 0 : entry?.zarAmount,
            })),
        },
        isMock: isMockPosition,
      }),
    [isMockPosition, rawHistoryItems]
  );
  const details = useMemo(
    () =>
      buildZar8YieldPositionDetails({
        ledgerEntry,
        marketAssets,
        performanceSeries,
        positionSnapshot,
      }),
    [ledgerEntry, marketAssets, performanceSeries, positionSnapshot]
  );
  const historyItems = useMemo(
    () =>
      buildZar8YieldPositionHistory({
        history: rawHistoryItems,
        marketAssets,
        isMock: isMockPosition,
      }),
    [isMockPosition, marketAssets, rawHistoryItems]
  );
  useEffect(() => {
    if (!ledgerId && !positionSnapshot) {
      return;
    }

    emitZar8YieldPositionEvent("zar8_yield_position_viewed", {
      ledgerId: ledgerId || positionSnapshot?.ledgerId,
      productId: liveProductId,
      title: details.title,
    });
  }, [details.title, ledgerId, liveProductId, positionSnapshot]);

  const handleRetry = () => {
    positionQuery.refetch();
    productHistoryQuery.refetch();
  };

  const handleUnlock = () => {
    emitZar8YieldPositionEvent("zar8_yield_position_unlock_clicked", {
      ledgerId: ledgerId || positionSnapshot?.ledgerId,
      productId: liveProductId,
      title: details.title,
    });

    persistZar8LockFlowState({
      ...createDefaultZar8LockFlowState(),
      isOpen: true,
      flowType: ZAR8_YIELD_FLOW_TYPES.unlock,
      step: ZAR8_LOCK_FLOW_STEPS.amount,
      selectedUnlockPositionId: ledgerId || positionSnapshot?.id || "",
    });

    navigate("/zar8/track/unlock");
  };

  const handleOpenYield = () => {
    const durationPeriod =
      ledgerEntry?.productId?.duration?.period ??
      ledgerEntry?.productId?.durationInYearOrMonth?.[0] ??
      positionSnapshot?.rawEntry?.productId?.duration?.period ??
      positionSnapshot?.rawEntry?.productId?.durationInYearOrMonth?.[0] ??
      positionSnapshot?.rawEntry?.duration?.period;
    const durationUnit =
      ledgerEntry?.productId?.duration?.unit ??
      ledgerEntry?.productId?.durationInYearOrMonth?.[1] ??
      positionSnapshot?.rawEntry?.productId?.duration?.unit ??
      positionSnapshot?.rawEntry?.productId?.durationInYearOrMonth?.[1] ??
      positionSnapshot?.rawEntry?.duration?.unit;
    const selectedDurationId = getZar8DurationIdFromParts(
      durationPeriod,
      durationUnit
    );
    const selectedYieldAssetId =
      details.yieldAssetSymbol?.toLowerCase() === "xaut" ? "xaut" : "btc";

    persistZar8LockFlowState({
      ...createDefaultZar8LockFlowState(),
      isOpen: true,
      flowType: ZAR8_YIELD_FLOW_TYPES.lock,
      step: ZAR8_LOCK_FLOW_STEPS.configuration,
      selectedYieldAssetId,
      selectedDurationId,
    });

    navigate("/zar8/track/lock");
  };

  if (!ledgerId && !positionSnapshot) {
    return (
      <main className="px-5 pb-12 pt-3 md:px-8 xl:px-[52px]">
        <Toaster />
        <SurfaceState
          title="Position unavailable"
          body="We couldn’t determine which yield position to open."
          actionLabel="Back to Track"
          onAction={() => navigate(returnTo)}
        />
      </main>
    );
  }

  if (positionQuery.isLoading && !positionSnapshot && !isMockPosition) {
    return (
      <main className="px-5 pb-12 pt-3 md:px-8 xl:px-[52px]">
        <Toaster />
        <div className="grid gap-3 xl:grid-cols-[minmax(0,1.03fr)_minmax(360px,0.97fr)]">
          <div className="h-[740px] animate-pulse rounded-[24px] border border-[#EBE9EF] bg-white" />
          <div className="h-[740px] animate-pulse rounded-[24px] border border-[#EBE9EF] bg-white" />
        </div>
      </main>
    );
  }

  const shouldShowPageError =
    positionQuery.isError && !positionSnapshot && !isMockPosition;

  if (shouldShowPageError) {
    return (
      <main className="px-5 pb-12 pt-3 md:px-8 xl:px-[52px]">
        <Toaster />
        <SurfaceState
          title="Position unavailable"
          body="We couldn’t load this yield position right now."
          actionLabel="Retry"
          onAction={handleRetry}
        />
      </main>
    );
  }

  return (
    <main className="px-5 pb-12 pt-3 md:px-8 xl:px-[52px]">
      <Toaster />

      <div className="grid gap-3 xl:grid-cols-[minmax(0,1.03fr)_minmax(360px,0.97fr)]">
        <section className="rounded-[24px] border border-[#EBE9EF] bg-white px-6 py-[45px] xl:min-h-[740px]">
          <div className="flex flex-col gap-[45px]">
            <div className="flex flex-col items-start gap-[45px]">
              <div className="flex w-full flex-col gap-6 xl:h-12 xl:flex-row xl:items-center xl:justify-between">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => navigate(returnTo)}
                    className="flex h-12 w-12 items-center justify-center rounded-full border border-[#EBE9EF] text-[#6D6584] transition-colors hover:bg-[#FAF9FC]"
                    aria-label="Back to track overview"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                  <p className="text-[20px] font-semibold leading-7 text-[#6D6584]">
                    {details.title}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={handleUnlock}
                  className={cn(
                    "flex h-12 items-center justify-center rounded-full border border-[#F1EDF7] px-6 text-[14px] font-semibold leading-5 text-[#6D6584] transition-colors hover:bg-[#FAF9FC]"
                  )}
                >
                  Unlock
                </button>
              </div>

              <div className="w-full">
                <div className="flex flex-col gap-4">
                  <AssetChip label={details.assetSymbol} />

                  <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
                    <p className="text-[32px] font-bold leading-10 text-[#423E51]">
                      {formatZar8YieldBtcValue(
                        details.currentBtcEquivalent,
                        details.yieldAssetSymbol
                      )}
                    </p>
                    <p
                      className={cn(
                        "text-[14px] font-semibold leading-5",
                        details.deltaValue >= 0
                          ? "text-[#17C3A5]"
                          : "text-[#DA2F58]"
                      )}
                    >
                      {details.deltaValue >= 0 ? "+" : "-"}
                      {formatFiatWithTwoDecimals(
                        Math.abs(details.deltaValue)
                      ).replace("R ", "R")}{" "}
                      ({details.deltaPercentage >= 0 ? "+" : "-"}
                      {Math.abs(details.deltaPercentage).toFixed(0)}%)
                    </p>
                  </div>

                  <p className="text-[12px] leading-[14px] text-[#676767]">
                    {details.yieldAssetSymbol} equivalent of current value
                  </p>
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center gap-[10px]">
                <p className="text-[16px] font-semibold leading-6 text-[#423E51]">
                  Progress to Maturity
                </p>
              </div>

              <div className="mt-3 h-2 w-full overflow-hidden rounded-[4px] bg-[#EBE9EF]">
                <div
                  className="h-full rounded-[4px]"
                  style={{
                    width: `${details.progressCompleted}%`,
                    background:
                      "linear-gradient(183.24deg, #EB3366 13.24%, #9747FF 130.13%)",
                  }}
                />
              </div>

              <div className="mt-2 flex items-center justify-between text-[14px] leading-5 text-[#6D6584]">
                <p>{details.progressCompleted}%</p>
                <p>{details.progressRemaining}% remaining</p>
              </div>
            </div>

            <div className="grid gap-8 xl:grid-cols-4 xl:gap-[50px]">
              <Metric
                label="Current ZAR8"
                value={formatFiatWithTwoDecimals(details.currentValue)}
              />
              <Metric
                label="Locked Value"
                value={formatFiatWithTwoDecimals(details.lockedValue)}
              />
              <Metric
                label="Start Date"
                value={formatZar8YieldPositionDate(details.startDate)}
              />
              <Metric
                label="Maturity Date"
                value={formatZar8YieldPositionDate(details.maturityDate)}
              />
            </div>

            <div className="px-[5px] pt-1">
              <Zar8YieldPositionChart
                series={performanceSeries}
                isLoading={
                  productHistoryQuery.isLoading &&
                  !isMockPosition &&
                  performanceSeries.length === 0
                }
                hasError={
                  productHistoryQuery.isError &&
                  !isMockPosition &&
                  performanceSeries.length === 0
                }
                onRetry={handleRetry}
              />
            </div>
          </div>
        </section>

        <aside className="overflow-hidden rounded-[24px] border border-[#EBE9EF] bg-white px-6 py-6 xl:min-h-[740px]">
          <div className="flex items-center justify-between gap-4">
            <p className="text-[20px] font-semibold leading-7 text-[#6D6584]">
              Yield history
            </p>

            <button
              type="button"
              onClick={handleOpenYield}
              className="flex h-12 items-center justify-center gap-2 rounded-full border border-[#F1EDF7] px-6 text-[14px] font-semibold leading-5 text-[#6D6584] transition-colors hover:bg-[#FAF9FC]"
            >
              <Plus className="h-4 w-4" />
              Yield
            </button>
          </div>

          <div className="mt-2 flex h-[680px] flex-col gap-2 overflow-y-auto pr-1">
            {productHistoryQuery.isLoading && !isMockPosition
              ? Array.from({ length: 4 }).map((_, index) => (
                  <div
                    key={index}
                    className="h-[94px] animate-pulse rounded-[16px] border border-[#F1EDF7] bg-[#FAF9FC]"
                  />
                ))
              : null}

            {productHistoryQuery.isError && !isMockPosition ? (
              <SurfaceState
                title="History unavailable"
                body="We couldn’t load this position’s yield history."
                actionLabel="Retry"
                onAction={handleRetry}
              />
            ) : null}

            {!productHistoryQuery.isLoading &&
            !productHistoryQuery.isError &&
            historyItems.length === 0 ? (
              <SurfaceState
                title="No history yet"
                body="History events will appear here once this position records activity."
              />
            ) : null}

            {!productHistoryQuery.isLoading &&
            !productHistoryQuery.isError &&
            historyItems.length > 0
              ? historyItems.map((item) => (
                  <HistoryItem key={item.id} item={item} />
                ))
              : null}
          </div>
        </aside>
      </div>
    </main>
  );
}
